import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Shield, BookOpen, Lock, Heart, CheckCircle, FileCheck } from 'lucide-react';

const trustPoints = [
  {
    icon: <BookOpen size={20} strokeWidth={1.5} />,
    title: 'Plain Language, Always',
    desc: 'Every report is translated from clinical psychology into warm, simple language any parent can understand — without jargon, without fear.',
    color: '#2A9D8F',
  },
  {
    icon: <Shield size={20} strokeWidth={1.5} />,
    title: 'Advisory, Not Diagnostic',
    desc: 'MindVrik does not diagnose medical or psychiatric conditions. We offer structured developmental guidance — safe, supportive, and non-clinical.',
    color: '#8DA399',
  },
  {
    icon: <Lock size={20} strokeWidth={1.5} />,
    title: 'Your Data Stays Yours',
    desc: 'All personal and assessment data is encrypted, stored securely, and never shared with third parties without explicit consent.',
    color: '#2A9D8F',
  },
  {
    icon: <Heart size={20} strokeWidth={1.5} />,
    title: 'Human Oversight, Always',
    desc: 'AI generates insights, but every counseling interaction is human-led. Expert counselors review reports before sessions.',
    color: '#F4A261',
  },
  {
    icon: <FileCheck size={20} strokeWidth={1.5} />,
    title: 'Research-Backed Methodology',
    desc: 'Our assessments are grounded in established psychological frameworks — not guesswork. Each test is designed, reviewed, and validated.',
    color: '#8DA399',
  },
  {
    icon: <CheckCircle size={20} strokeWidth={1.5} />,
    title: 'Guidance, Not Pressure',
    desc: 'MindVrik never tells your child what to do. We illuminate possibilities and support informed choices — at every step.',
    color: '#2A9D8F',
  },
];

const WhyParentsTrust = () => {
  const [ref, visible] = useScrollReveal();

  return (
    <section
      ref={ref}
      style={{ padding: '100px 24px', background: '#F9F9F7', position: 'relative', overflow: 'hidden' }}
      data-testid="why-parents-section"
    >
      {/* Subtle background texture */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/6caceefd-cb80-4c8a-a8fe-90917c71e43e/images/af1be90ab9b74428d228281b623fa5d3c9f703c86ebc6b2c71a22742a338d310.png')`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        opacity: 0.04, pointerEvents: 'none',
      }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, rgba(42,157,143,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">For Parents</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Why Parents Trust<br />
            <span style={{ color: '#2A9D8F', fontWeight: 300 }}>MindVrik</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '480px', margin: '0 auto', lineHeight: 1.7 }}>
            Built with parents at the center — clear, safe, and genuinely useful at every step of your child's growth journey.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {trustPoints.map((tp, i) => (
            <div
              key={tp.title}
              className={`card-lift reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''}`}
              data-testid={`trust-point-${i}`}
              style={{
                background: 'rgba(255,255,255,0.78)',
                backdropFilter: 'blur(14px)',
                border: `1px solid ${tp.color}20`,
                borderRadius: '20px',
                padding: '28px 28px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{
                width: '48px', height: '48px', borderRadius: '14px',
                background: `${tp.color}12`, border: `1px solid ${tp.color}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: tp.color, marginBottom: '18px',
              }}>{tp.icon}</div>
              <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '16px', color: '#1A202C', margin: '0 0 10px' }}>{tp.title}</h3>
              <p style={{ fontFamily: 'Inter', fontSize: '14px', color: '#64748B', lineHeight: 1.7, margin: 0 }}>{tp.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyParentsTrust;
